// ═══════════════════════════════════════════
// MonArgent Pro — Helpers & Constants
// ═══════════════════════════════════════════

// ─── Currency ───
export const CURRENCY = "FCFA";
export const CURRENCY_SHORT = "F";

// ─── Format money ───
export function fm(n) {
  if (n === undefined || n === null || isNaN(n)) return "0";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function fmFull(n) {
  return `${fm(n)} ${CURRENCY}`;
}

// ─── Generate ID ───
export function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// ─── Date helpers ───
export function today() {
  return new Date().toISOString().slice(0, 10);
}

export function now() {
  return new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const DAYS_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const MONTHS_FULL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export function dayName(d) {
  const dt = new Date(d);
  return `${DAYS_FR[dt.getDay()]} ${dt.getDate()} ${MONTHS_FR[dt.getMonth()]}`;
}

export function dayNameFull(d) {
  const dt = new Date(d);
  return `${DAYS_FR[dt.getDay()]} ${dt.getDate()} ${MONTHS_FULL[dt.getMonth()]} ${dt.getFullYear()}`;
}

export function monthName(d) {
  const dt = new Date(d);
  return `${MONTHS_FULL[dt.getMonth()]} ${dt.getFullYear()}`;
}

// ─── Business types ───
export const BUSINESS_TYPES = [
  { key: "taxi", icon: "🚕", label: "Transport / Taxi", color: "#E67E22" },
  { key: "resto", icon: "🍽️", label: "Restaurant / Maquis", color: "#E74C3C" },
  { key: "boutique", icon: "🛍️", label: "Boutique / Commerce", color: "#9B59B6" },
  { key: "salon", icon: "💇", label: "Salon de coiffure", color: "#E91E8C" },
  { key: "pharma", icon: "💊", label: "Pharmacie", color: "#00BCD4" },
  { key: "agri", icon: "🌾", label: "Agriculture", color: "#27AE60" },
  { key: "autre", icon: "📦", label: "Autre activité", color: "#3498DB" },
];

export function getBizType(key) {
  return BUSINESS_TYPES.find((b) => b.key === key) || BUSINESS_TYPES[6];
}

// ─── Mobile Money providers ───
export const MOMO_PROVIDERS = [
  { key: "orange", label: "Orange Money", color: "#FF6600", icon: "🟠" },
  { key: "mtn", label: "MTN MoMo", color: "#FFCC00", icon: "🟡" },
  { key: "wave", label: "Wave", color: "#1DC3E2", icon: "🔵" },
  { key: "mpesa", label: "M-Pesa", color: "#4CAF50", icon: "🟢" },
  { key: "moov", label: "Moov Money", color: "#0066CC", icon: "🔷" },
  { key: "free", label: "Free Money", color: "#CD1F25", icon: "🔴" },
];

export function getMomoProvider(key) {
  return MOMO_PROVIDERS.find((p) => p.key === key);
}

// ─── Employee avatars ───
export const AVATARS = ["👨🏾", "👩🏾", "👨🏿", "👩🏿", "🧑🏾", "🧔🏾", "👱🏾‍♀️", "🧕🏾", "👲🏾", "🧑🏿"];

// ─── Theme / Style constants ───
export const THEME = {
  bg: "#FFFBF5",
  card: "#FFFFFF",
  border: "#F0ECE4",
  text: "#1a1a1a",
  textSub: "#999999",
  green: "#27AE60",
  red: "#E74C3C",
  gold: "#F39C12",
  whatsapp: "#075E54",
  whatsappLight: "#25D366",
  chatBg: "#ECE5DD",
  chatUser: "#DCF8C6",
  chatBot: "#FFFFFF",
  font: "'Nunito', sans-serif",
};

// ─── Detect platform ───
export function getPlatform() {
  if (window.electronAPI?.isElectron) return "electron";
  if (window.matchMedia("(display-mode: standalone)").matches) return "pwa";
  if (navigator.standalone) return "pwa-ios";
  return "browser";
}

export function isDesktop() {
  return window.innerWidth >= 768;
}

export function isMobile() {
  return window.innerWidth < 768;
}

// ─── Vibrate (mobile) ───
export function vibrate(pattern = [50]) {
  if ("vibrate" in navigator) {
    navigator.vibrate(pattern);
  }
}

// ─── Sound effects ───
export function playSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.value = 0.1;

    switch (type) {
      case "success":
        osc.frequency.value = 800;
        osc.start();
        osc.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.1);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
        osc.stop(ctx.currentTime + 0.2);
        break;
      case "error":
        osc.frequency.value = 300;
        osc.start();
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        osc.stop(ctx.currentTime + 0.3);
        break;
      case "notification":
        osc.frequency.value = 600;
        osc.type = "sine";
        osc.start();
        osc.frequency.linearRampToValueAtTime(900, ctx.currentTime + 0.15);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
        osc.stop(ctx.currentTime + 0.3);
        break;
      default:
        osc.frequency.value = 500;
        osc.start();
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
        osc.stop(ctx.currentTime + 0.1);
    }
  } catch {
    // Audio not available
  }
}
